import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LinePriceTagAltIcon from 'LuxKit/Icons/line/LinePricetagAltIcon'
import Label from 'LuxKit/Label/Label'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import Caption from 'LuxKit/Typography/Caption'
import Arrow from 'components/utils/Arrows/Arrow'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

const ItemContainer = styled.div`
  padding: ${rem(12)};
  cursor: pointer;

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }
`
const DepartureContent = styled.div`
  width: ${rem(95)};
`

interface Props {
  onClick: (departureDate: string | null) => void
  departureDate: string | null
  arrivalDate?: string
  price?: number
  isCheapest?: boolean
  hasSelectedDates?: boolean
}

function CruiseModalPricesItem({
  onClick,
  departureDate,
  arrivalDate,
  price,
  isCheapest,
  hasSelectedDates,
}: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const handleClick = useCallback(() => {
    onClick(departureDate)
  }, [departureDate, onClick])

  const formattedDepartureDate = dateFormatter(departureDate, INTL_SHORT_DAY_NAME_DAY_MONTH)
  const formattedArrivalDate = arrivalDate
    ? dateFormatter(arrivalDate, INTL_SHORT_DAY_NAME_DAY_MONTH)
    : undefined

  return <ItemContainer onClick={handleClick} data-testid="cruise-modal-price-item-click">
    <Group direction="horizontal" horizontalAlign="space-between">
      <Group direction="vertical" horizontalAlign="start" verticalAlign="center" gap={4}>
        <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
          {isCheapest && <PromotionLabel
            variant="default"
            icon={<LinePriceTagAltIcon />}
          >{intl.formatMessage({
              id: 'tgfJoHvpXMy1-iBU0-ge',
              defaultMessage: 'Cheapest',
              description: 'Label indicating the cheapest cruise departure option',
            })}
          </PromotionLabel>}

          {hasSelectedDates && <Label
            kind="plain"
            variant="highlight-secondary"
            startIcon={<LineCheckCircleIcon />}
          >{intl.formatMessage({
              id: 'WCLZUGsrCiPhO7yVHA0A',
              defaultMessage: 'Selected dates',
              description: 'Label indicating the departure matches user selected dates',
            })}
          </Label>}
        </Group>

        <Group direction="horizontal" verticalAlign="center">
          <DepartureContent>
            {formattedDepartureDate}
          </DepartureContent>

          {!!formattedArrivalDate && <Group direction="horizontal" verticalAlign="center" gap={12}>
            <LineArrowRightIcon />
            {formattedArrivalDate}
          </Group>}
        </Group>
      </Group>
      <Group direction="horizontal" verticalAlign="center" horizontalAlign="end" gap={8}>
        {!!price && <Group direction="vertical" gap={4}>
          <Caption align="end" variant="medium">{intl.formatMessage({
            id: '9TB8GTPygGWVjq-JG0OH',
            defaultMessage: 'From',
            description: 'Price caption indicating starting price for cruise departure',
          })}
          </Caption>
          <PriceRowPrice
            size="S"
            price={price}
            saleUnit=""
          />
        </Group>}
        <Arrow />
      </Group>
    </Group>
  </ItemContainer>
}

export default CruiseModalPricesItem
